import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { ChevronDown, FileSignature, IdCard, Monitor, Plus, ScanFace, Search, UserCheck, UserRound } from 'lucide-react';
import VerificationBreadcrumb from '../components/VerificationBreadcrumb';
import { SelectionActionBar, SelectionCheckbox, useTableSelection } from '../components/TableSelection';
import { getBucketStatus, matchesBucket } from '../lib/statusModel';
import { lastUpdatedTimestamp } from '../lib/timestamps';

const STATUS_FILTERS = [
  { v: '', label: 'All' },
  { v: 'approved', label: 'Approved' },
  { v: 'needs_review', label: 'Action required' },
  { v: 'pending', label: 'Pending' },
  { v: 'failed', label: 'Failed' },
];

const REVIEWER_FILTERS = [
  { v: '', label: 'Anyone' },
  { v: 'me', label: 'Me' },
  { v: 'unassigned', label: 'Unassigned' },
];

const verificationBadgeStyles = {
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  rejected: 'bg-red-50 text-red-700 border-red-300',
  needs_review: 'bg-amber-50 text-amber-800 border-amber-300',
  needs_action: 'bg-amber-50 text-amber-800 border-amber-300',
  pending: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  created: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  not_received: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  opened: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  in_progress: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  processing: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  expired: 'bg-red-50 text-red-700 border-red-300',
  revoked: 'bg-red-50 text-red-700 border-red-300',
};

const verificationBadgeLabels = {
  approved: 'Approved',
  rejected: 'Rejected',
  needs_review: 'Action required',
  pending: 'Pending',
  opened: 'Pending',
  in_progress: 'Pending',
  processing: 'Pending',
  expired: 'Expired',
  revoked: 'Revoked',
};

function VerificationStatusBadge({ status }) {
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 uppercase tracking-widest text-[10px] font-mono border ${verificationBadgeStyles[status] || verificationBadgeStyles.pending}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70"/>
      {verificationBadgeLabels[status] || String(status || 'pending').replaceAll('_', ' ')}
    </span>
  );
}

function BadgeSequence({ children }) {
  const items = React.Children.toArray(children).filter(Boolean);
  return (
    <span className="inline-flex items-center">
      {items.map((child, index) => (
        <React.Fragment key={index}>
          {index > 0 && <span aria-hidden="true" className="h-px w-2 bg-zinc-200"/>}
          {child}
        </React.Fragment>
      ))}
    </span>
  );
}

function MethodBadge({ item }) {
  const idOnly = isIdvVerification(item);
  const method = idOnly ? 'IDV' : 'KYC';
  const ageLabel = ageRequirementLabel(item?.min_age);
  return (
    <div className="flex flex-wrap items-center">
      <BadgeSequence>
      <span className="inline-flex h-6 items-center gap-1.5 rounded-full bg-white px-2 py-0.5 uppercase tracking-widest text-[10px] font-mono border text-zinc-500 border-zinc-200">
        {idOnly ? <IdCard className="w-3 h-3" strokeWidth={1.8}/> : <UserCheck className="w-3 h-3" strokeWidth={1.8}/>}
        {method}
      </span>
      {ageLabel && <AgeRequirementBadge value={item?.min_age}/>}
      </BadgeSequence>
    </div>
  );
}

function WorkflowStepBadge({ icon: Icon, title, variant = 'default' }) {
  const variantClass = variant === 'external'
    ? 'rounded-lg border-dashed border-zinc-200 bg-white text-zinc-400 hover:text-zinc-500'
    : 'rounded-full border-zinc-200 bg-white text-zinc-400 hover:text-zinc-500';
  return (
    <span className={`inline-flex h-6 min-w-6 items-center justify-center border px-1.5 ${variantClass}`} title={title} aria-label={title}>
      <Icon className="h-3 w-3" strokeWidth={1.8}/>
    </span>
  );
}

function ageRequirementLabel(value) {
  const age = Number(value);
  return [16, 18, 21].includes(age) ? `${age}+` : '';
}

function AgeRequirementBadge({ value }) {
  const label = ageRequirementLabel(value);
  if (!label) return null;
  return (
    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full border border-zinc-200 bg-white px-1.5 text-[10px] font-mono tracking-widest text-zinc-500" title={`Age requirement: ${label}`} aria-label={`Age requirement: ${label}`}>
      {label}
    </span>
  );
}

function isIdvVerification(item) {
  const method = String(item?.verification_method || item?.method || '').toLowerCase();
  return method === 'idv' || method === 'id_only' || item?.requires_selfie === false;
}

function WorkflowBadges({ item }) {
  const idOnly = isIdvVerification(item);
  const linkedToDocument = !!(
    item?.linked_signature_id ||
    item?.signature_id ||
    item?.linked_document_id ||
    item?.related_type === 'signature'
  );
  return (
    <BadgeSequence>
      {item?.device_ip_analysis_enabled && <WorkflowStepBadge icon={Monitor} title="Device and IP analysis"/>}
      <WorkflowStepBadge icon={IdCard} title="ID check"/>
      {!idOnly && <WorkflowStepBadge icon={ScanFace} title="Selfie check"/>}
      {linkedToDocument && <WorkflowStepBadge icon={FileSignature} title="Document signing" variant="external"/>}
    </BadgeSequence>
  );
}

function MissingIdentifierText() {
  return (
    <div className="min-w-0">
      <div className="font-mono text-xs text-zinc-500 truncate">Not received</div>
      <div className="mt-1 text-xs text-zinc-500 leading-4">Unknown</div>
    </div>
  );
}

function OnboardingIdentifierText() {
  return (
    <div className="min-w-0">
      <div className="font-mono text-xs text-zinc-500 truncate">Onboarding identity</div>
    </div>
  );
}

function formatDocumentType(value) {
  if (!value) return '';
  const labels = {
    id_card: 'ID card',
    passport: 'Passport',
    residence_permit: 'Residence permit',
  };
  const normalized = String(value).trim().toLowerCase();
  return labels[normalized] || normalized
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [assigned, setAssigned] = useState(''); // '' | 'me' | 'unassigned'
  const [q, setQ] = useState('');
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [reviewerMenuOpen, setReviewerMenuOpen] = useState(false);

  const load = useCallback(async ({ q: search, assignedTo } = {}) => {
    setLoading(true);
    const { data } = await api.get('/verification-sessions', { params: {
      q: search || undefined,
      assigned_to: assignedTo || undefined,
    } });
    setItems(data.items); setLoading(false);
  }, []);

  useEffect(() => { load({ assignedTo: assigned }); }, [assigned, load]);

  const counts = useMemo(() => {
    const c = { all: items.length, approved: 0, needs_review: 0, pending: 0, failed: 0 };
    items.forEach((i) => {
      const bucket = getBucketStatus(i, 'verification');
      if (bucket === 'pending') c.pending++;
      else if (bucket === 'failed') c.failed++;
      else if (bucket === 'action_required') c.needs_review++;
      else if (i.status === 'approved') c.approved++;
    });
    return c;
  }, [items]);
  const filteredItems = useMemo(
    () => items.filter((item) => matchesBucket(item, status, 'verification')),
    [items, status]
  );
  const tableSelection = useTableSelection(filteredItems);
  const selectedStatusLabel = STATUS_FILTERS.find((filter) => filter.v === status)?.label || 'All';
  const statusButtonLabel = status ? `Status: ${selectedStatusLabel}` : 'Status';
  const selectedReviewerLabel = REVIEWER_FILTERS.find((filter) => filter.v === assigned)?.label || 'Anyone';
  const reviewerButtonLabel = assigned ? `Reviewer: ${selectedReviewerLabel}` : 'Reviewer';

  return (
    <div data-testid="dashboard-page">
      <VerificationBreadcrumb />

      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Verifications</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Individuals</h1>
        </div>
        <button data-testid="new-session-btn" onClick={() => navigate('/dashboard/verifications/new')}
          className="inline-flex items-center gap-2 bg-black text-white text-sm px-3 py-2 rounded-sm hover:bg-zinc-800">
          <Plus className="w-4 h-4" strokeWidth={2}/> New verification
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { k:'approved', label:'Approved', v:counts.approved },
          { k:'needs_review', label:'Action required', v:counts.needs_review },
          { k:'pending', label:'Pending', v:counts.pending },
          { k:'failed', label:'Failed', v:counts.failed },
        ].map((s) => (
          <div key={s.k} className="border border-zinc-200 rounded-sm bg-white p-4" data-testid={`stat-${s.k}`}>
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">{s.label}</div>
            <div className="font-display text-2xl font-bold mt-1">{s.v}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 justify-between mb-4">
        <div className="flex flex-wrap items-center gap-1">
        <div
          className="relative"
          data-testid="status-filter"
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              setStatusMenuOpen(false);
            }
          }}
        >
          <button
            type="button"
            onClick={() => setStatusMenuOpen((open) => !open)}
            className="h-[34px] inline-flex items-center gap-2 border border-zinc-200 rounded-sm bg-white px-3 text-[10px] font-medium uppercase tracking-widest text-zinc-500 hover:border-zinc-300"
            aria-haspopup="listbox"
            aria-expanded={statusMenuOpen}
          >
            {statusButtonLabel}
            <ChevronDown className="w-3.5 h-3.5 text-zinc-400" strokeWidth={1.5}/>
          </button>
          {statusMenuOpen && (
            <div className="absolute left-0 top-[calc(100%+4px)] z-20 w-44 border border-zinc-200 rounded-sm bg-white py-1">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.v}
                  type="button"
                  onClick={() => {
                    setStatus(f.v);
                    setStatusMenuOpen(false);
                  }}
                  data-testid={`filter-${f.v || 'all'}`}
                  className={`block w-full text-left px-3 py-2 text-[10px] font-medium uppercase tracking-widest ${status === f.v ? 'text-zinc-500' : 'text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50'}`}
                  role="option"
                  aria-selected={status === f.v}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div
          className="relative"
          data-testid="assigned-filter"
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              setReviewerMenuOpen(false);
            }
          }}
        >
          <button
            type="button"
            onClick={() => setReviewerMenuOpen((open) => !open)}
            className="h-[34px] inline-flex items-center gap-2 border border-zinc-200 rounded-sm bg-white px-3 text-[10px] font-medium uppercase tracking-widest text-zinc-500 hover:border-zinc-300"
            aria-haspopup="listbox"
            aria-expanded={reviewerMenuOpen}
          >
            {reviewerButtonLabel}
            <ChevronDown className="w-3.5 h-3.5 text-zinc-400" strokeWidth={1.5}/>
          </button>
          {reviewerMenuOpen && (
            <div className="absolute left-0 top-[calc(100%+4px)] z-20 w-44 border border-zinc-200 rounded-sm bg-white py-1">
              {REVIEWER_FILTERS.map((f) => (
                <button
                  key={f.v}
                  type="button"
                  onClick={() => {
                    setAssigned(f.v);
                    setReviewerMenuOpen(false);
                  }}
                  data-testid={`assigned-${f.v || 'any'}`}
                  className={`block w-full text-left px-3 py-2 text-[10px] font-medium uppercase tracking-widest ${assigned === f.v ? 'text-zinc-500' : 'text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50'}`}
                  role="option"
                  aria-selected={assigned === f.v}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>
        </div>
        <div className="w-full sm:w-80 h-[34px] flex items-center gap-2 border border-zinc-200 rounded-sm bg-white px-3">
          <Search className="w-4 h-4 text-zinc-400" strokeWidth={1.5}/>
          <input data-testid="search-input" value={q} onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>{ if (e.key==='Enter') load({ q }); }}
            className="flex-1 h-full text-[10px] font-medium uppercase tracking-widest bg-transparent text-zinc-500 focus:outline-none placeholder:text-zinc-400" placeholder="Search by name, email or document number…"/>
        </div>
      </div>

      <SelectionActionBar count={tableSelection.selectedCount} onClear={tableSelection.clearSelection}/>
      <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white">
        <table className="w-full text-sm" data-testid="verifications-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-xs uppercase tracking-widest text-zinc-500">
              <th className="w-10 px-4 py-3 align-middle font-medium">
                <SelectionCheckbox
                  checked={tableSelection.allVisibleSelected}
                  indeterminate={tableSelection.someVisibleSelected && !tableSelection.allVisibleSelected}
                  onCheckedChange={tableSelection.toggleAllVisible}
                  ariaLabel="Select all visible verifications"
                />
              </th>
              <th className="text-left px-4 py-3 font-medium">Applicant</th>
              <th className="text-left px-4 py-3 font-medium">Identifier</th>
              <th className="text-left px-4 py-3 font-medium">Method</th>
              <th className="text-left px-4 py-3 font-medium">Workflow</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Last updated</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-12 text-sm text-zinc-500" data-testid="table-loading">Loading…</td></tr>
            ) : filteredItems.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-sm text-zinc-500" data-testid="table-empty">No verifications yet.</td></tr>
            ) : filteredItems.map((it) => {
              const isOnboarding = it.purpose === 'onboarding_identity' && it.is_platform_verification;
              const claimedName = [it.claimed_first_name, it.claimed_last_name].filter(Boolean).join(' ');
              const applicantName = isOnboarding
                ? (it.extracted_name || claimedName || it.applicant_email || 'Unnamed applicant')
                : (claimedName || it.applicant_email || 'Unnamed applicant');
              const documentType = formatDocumentType(it.extracted_document_type);
              const lastUpdatedAt = lastUpdatedTimestamp(it);
              const isSelected = tableSelection.isSelected(it.id);
              return (
              <tr key={it.id} className={`border-b border-zinc-100 cursor-pointer ${isSelected ? 'bg-zinc-50/80' : 'hover:bg-zinc-50/60'}`} data-testid={`verification-row-${it.id}`}
                  onClick={() => window.location.assign(`/verifications/${it.id}`)}>
                <td className="w-10 px-4 py-4 align-middle">
                  <SelectionCheckbox
                    checked={isSelected}
                    onCheckedChange={() => tableSelection.toggleRow(it.id)}
                    ariaLabel={`Select ${applicantName}`}
                  />
                </td>
                <td className="px-4 py-4 align-middle">
                  <div className="flex items-start gap-2 min-w-0">
                    <UserRound className="w-4 h-4 mt-0.5 text-zinc-400 shrink-0" strokeWidth={1.5}/>
                    <div className="min-w-0">
                      <div className="font-medium flex min-w-0 items-center gap-2">
                        <span className="truncate max-w-[12rem]" title={applicantName}>{applicantName}</span>
                        {it.sandbox && <span data-testid={`row-sandbox-${it.id}`} className="shrink-0 text-[10px] uppercase tracking-widest font-mono font-semibold px-1.5 py-0 border border-amber-300 bg-amber-50 text-amber-800">SBX</span>}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 align-middle">
                  {it.extracted_document_number ? (
                    <div className="min-w-0">
                      <div className="font-mono text-xs text-zinc-500 truncate">{it.extracted_document_number}</div>
                      {documentType && <div className="mt-1 text-xs text-zinc-500 leading-4">{documentType}</div>}
                    </div>
                  ) : isOnboarding ? (
                    <OnboardingIdentifierText/>
                  ) : (
                    <MissingIdentifierText/>
                  )}
                </td>
                <td className="px-4 py-4 align-middle">
                  <MethodBadge item={it}/>
                </td>
                <td className="px-4 py-4 align-middle">
                  <WorkflowBadges item={it}/>
                </td>
                <td className="px-4 py-4 align-middle">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <VerificationStatusBadge status={it.status}/>
                  </div>
                </td>
                <td className="px-4 py-4 align-middle font-mono text-xs text-zinc-500">{lastUpdatedAt ? new Date(lastUpdatedAt).toLocaleString() : ''}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
